import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import config from '../../config/config';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import { FaCalendar, FaUser, FaTag, FaShare, FaClock } from 'react-icons/fa';

const BlogContent2 = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/blogs/${slug}`);
        setBlog(response.data);
        
        // Fetch related posts based on tags
        const relatedResponse = await axios.get('/api/blogs/public');
        const allPosts = relatedResponse.data;
        const currentTags = response.data.tags || [];
        
        // Filter and sort related posts
        const related = allPosts
          .filter(post => 
            post.id !== response.data.id && // Exclude current post
            post.tags && 
            post.tags.some(tag => 
              currentTags.some(currentTag => 
                currentTag.toLowerCase().includes(tag.toLowerCase().replace(/["\[\]]/g, '').trim())
              )
            )
          )
          .slice(0, 3); // Get top 3 related posts
        
        setRelatedPosts(related);
        setError(null);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-white mb-4">{error}</h1>
        <p className="text-gray-400 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/blog"
          className="px-6 py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
        >
          Back to Blog
        </Link>
      </div>
    );
  }

  if (!blog) return null;

  return (
    <article className="py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {blog.title}
            </h1>
          </motion.div>

          {/* Featured Image */}
          {blog.imageUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative aspect-video rounded-3xl overflow-hidden mb-16"
            >
              <img
                src={`${config.API_BASE_URL}${blog.imageUrl}`}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}

          {/* Content Grid */}
          <div className="grid md:grid-cols-12 gap-12">
            {/* Meta Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="md:col-span-3 space-y-8"
            >
              {/* Author */}
              <div className="p-6 rounded-2xl bg-white/5">
                <div className="flex items-center gap-4 mb-4">
                  <div>
                    <h3 className="text-white font-medium">{blog.author || 'Anonymous'}</h3>
                  </div>
                </div>
              </div>

              {/* Meta Info */}
              <div className="p-6 rounded-2xl bg-white/5 space-y-4">
                <div className="flex items-center gap-3 text-gray-400">
                  <FaCalendar className="w-4 h-4" />
                  <span>
                    {new Date(blog.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <FaClock className="w-4 h-4" />
                  <span>{Math.ceil(blog.content.split(' ').length / 200)} min read</span>
                </div>
                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex items-center gap-3 text-gray-400">
                    <FaTag className="w-4 h-4" />
                    <span>{blog.tags[0].replace(/["\[\]]/g, '').trim()}</span>
                  </div>
                )}
              </div>

              {/* Share */}
              <div className="p-6 rounded-2xl bg-white/5">
                <h3 className="text-white font-medium mb-4">Share</h3>
                <div className="flex flex-col gap-3">
                  {['Twitter', 'Facebook', 'LinkedIn'].map((platform, index) => (
                    <button
                      key={index}
                      className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-white/10 text-gray-400 hover:bg-primary hover:text-white transition-all"
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="p-6 rounded-2xl bg-white/5 space-y-4"
                >
                  <h3 className="text-white font-medium mb-4">Related Posts</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-4">
                          {post.imageUrl && (
                            <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                              <img
                                src={`${config.API_BASE_URL}${post.imageUrl}`}
                                alt={post.title}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              />
                            </div>
                          )}
                          <div>
                            <h4 className="text-white font-semibold mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                              {post.title}
                            </h4>
                            <span className="text-sm text-gray-400">
                              {new Date(post.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.aside>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="md:col-span-9"
            >
              <div 
                className="prose prose-lg prose-invert max-w-none text-white"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(blog.content)
                }}
              />
            </motion.div>
            
          </div>
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-6 pt-12">
              {blog.tags.map((tag, index) => (
                <span key={index} className="inline-block px-4 py-2 bg-primary/90 backdrop-blur-sm rounded-full text-white text-sm">
                  {tag.replace(/["\[\]]/g, '').trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default BlogContent2;
